/**
 * Detail Component Tool — Annotate > Detail > Component (Detail Component).
 *
 * Places a single 2D annotation symbol (brick, block, bolt, etc.) that lives
 * only in the active view — model geometry is untouched. The symbol footprint
 * is paper-sized: it resizes with the view scale at render time (see
 * DetailComponentRenderer).
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import i18n from '../i18n';

export type DetailComponentSymbol = 'Brick' | 'Block' | 'Bolt' | 'Dot';

export interface DetailComponentGeometryData {
    position: [number, number, number];
    rotation: number; // degrees
    symbol: DetailComponentSymbol;
}

export interface DetailComponentElement extends Element {
    category: 'Detail Items';
    geometryData: DetailComponentGeometryData;
}

export class DetailComponentTool extends BaseTool {
    id = 'detail_component';
    name = 'Detail Component';
    cursor = 'crosshair';

    private rotation = 0; // degrees

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.detailComponentClickToPlaceSpace'));
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point, 0.1);
        this.createComponent(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.context.appStore.setToolPreview({
                type: 'detail_component',
                position: point,
                rotation: this.rotation,
            });
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.code === 'Space') {
            event.preventDefault();
            this.rotation = (this.rotation + 90) % 360;
            this.notify(i18n.t('tools.notify.detailRotation', { rotation: this.rotation }));
            return;
        }
        super.onKeyDown(event);
    }

    private createComponent(point: THREE.Vector3) {
        if (!this.context) return;

        const symbol: DetailComponentSymbol =
            this.context.bimStore.toolProperties.detailComponentSymbol ?? 'Brick';

        const geometryData: DetailComponentGeometryData = {
            position: [point.x, point.y, point.z],
            rotation: this.rotation,
            symbol,
        };

        const component: DetailComponentElement = {
            id: uuidv4(),
            category: 'Detail Items',
            type: `Detail Component: ${symbol}`,
            parameters: {
                'Symbol': symbol,
                'Rotation': `${this.rotation}°`,
            },
            constraints: [],
            geometryData,
        };

        this.context.bimStore.addElement(component);
    }
}
